import { useState, useEffect } from "react";
import { Modal, Form, Input, Button } from "antd";
import useMessage from "logic/src/utils/useMessage";
import { applyVideoEdit } from "logic/src/services/videoEdit";
import styles from "./VideoUnderstandingApplyDialog.module.scss";

const { TextArea } = Input;

interface VideoUnderstandingApplyDialogProps {
  visible: boolean;
  onCancel: () => void;
  onSubmitSuccess: () => void;
}

const VideoUnderstandingApplyDialog: React.FC<
  VideoUnderstandingApplyDialogProps
> = ({ visible, onCancel, onSubmitSuccess }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [reasonLength, setReasonLength] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const messageApi = useMessage();

  // 监听reason字段的变化
  useEffect(() => {
    const subscription = form
      .getFieldInstance("reason")
      ?.addEventListener?.("input", () => {
        const value = form.getFieldValue("reason") || "";
        setReasonLength(value.length);
      });

    return () => {
      subscription?.remove?.();
    };
  }, [form]);

  // 处理倒计时
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0 && submitSuccess) {
      // 倒计时结束，关闭弹窗
      onCancel();
      setSubmitSuccess(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [countdown, onCancel, submitSuccess]);

  // 表单提交处理
  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setSubmitting(true);

      // 调用申请试用的API
      const formData = form.getFieldsValue();
      try {
        const response = await applyVideoEdit({
          email: formData.email,
          reason: formData.reason,
        });

        if (response.code === 0) {
          // 申请成功
          setCountdown(3); // 设置3秒倒计时
          setTimeout(() => {
            setSubmitSuccess(true);
          }, 1000);
          // messageApi.success("您的试用申请已提交，我们将尽快处理");
          // form.resetFields();
          // setReasonLength(0);
          onSubmitSuccess();
        } else {
          // 请求成功但业务失败
          messageApi.error("申请失败，请稍后再试");
          setSubmitting(false);
        }
      } catch (apiError) {
        console.error("API请求失败:", apiError);
        messageApi.error("网络请求失败，请检查网络连接");
        setSubmitting(false);
      }
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  };

  // 获取按钮文案
  const getButtonText = () => {
    if (submitting && !submitSuccess) {
      return "提交中...";
    }
    if (submitSuccess) {
      return `申请已提交（${countdown}s)`;
    }
    return "提交";
  };

  // 获取按钮样式
  const getButtonStyle = () => {
    if (submitting || submitSuccess) {
      return {
        background: "#EDEEFF",
        color: "#5D63C5",
      };
    }
    return {};
  };

  return (
    <Modal
      open={visible}
      footer={null}
      onCancel={onCancel}
      width={440}
      centered
      closable={true}
      maskClosable={false}
      className={styles.applyDialog}
    >
      <div className={styles.applyContainer}>
        <h2 className={styles.applyTitle}>申请试用</h2>

        <Form form={form} layout="vertical" className={styles.applyForm}>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: "请输入邮箱" },
              { type: "email", message: "请输入有效的邮箱格式" },
            ]}
          >
            <Input placeholder="请填写邮箱" />
          </Form.Item>

          <Form.Item
            name="reason"
            label="申请原因"
            rules={[{ required: true, message: "请输入申请原因" }]}
          >
            <div className={styles.textAreaWrapper}>
              <TextArea
                placeholder="请输入申请原因"
                rows={4}
                maxLength={100}
                showCount={false}
                className={styles.reasonTextArea}
                onChange={e => setReasonLength(e.target.value.length)}
              />
              <div className={styles.textAreaCount}>{reasonLength}/100</div>
            </div>
          </Form.Item>

          <Form.Item className={styles.submitButtonContainer}>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting && !submitSuccess}
              disabled={submitting || submitSuccess}
              className={styles.submitButton}
              style={getButtonStyle()}
            >
              {getButtonText()}
            </Button>
          </Form.Item>
        </Form>

        {messageApi.contextHolder}
      </div>
    </Modal>
  );
};

export default VideoUnderstandingApplyDialog;
